<template>
  <UIModal v-model="isOpen" back-button scrollable @back="$emit('back')">
    <template #header>
      <h2 class="tw:font-serif tw:font-bold tw:text-white tw:text-3xl tw:text-center">
        {{ $t('halloweenCharacterHireModalHireTitle') }}
      </h2>
    </template>
    <div class="tw:px-8 tw:max-w-4xl tw:h-full">
      <div class="tw:pb-2 tw:overflow-x-auto">
        <div class="tw:flex tw:justify-start tw:gap-2">
          <CharacterCard
            v-for="{ name, stats, cost, status, type, initialData } in availableCharacters"
            :key="type"
            :stats
            :cost
            :status
            :name
            :character="initialData"
            class="tw:shrink-0"
            :data-type="type"
            :model-value="halloween.$state.runtimeSelectedCharacters.find((c) => c.characterType === type)"
            @update:model-value="
              $event
                ? (halloween.$state.runtimeSelectedCharacters = [...halloween.$state.runtimeSelectedCharacters, $event])
                : (halloween.$state.runtimeSelectedCharacters = halloween.$state.runtimeSelectedCharacters.filter(
                    (c) => c.characterType !== type
                  ))
            "
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:font-serif">
        <div class="tw:flex tw:flex-col tw:items-center tw:text-white">
          <p class="tw:font-bold tw:text-2xl">{{ $t('halloweenCharacterHireModalHireTotalPriceText') }}</p>
          <ul class="tw:flex tw:justify-center tw:gap-2">
            <li v-for="item in totalPrice" :key="item.address" class="tw:inline-flex tw:items-center tw:gap-1">
              <img class="tw:w-10 tw:h-10" :src="halloweenItemRegistry.resolveIcon(item)" />
              <span>
                {{ $t(item.name, 0) }}:
                <span class="tw:place-self-end">
                  {{
                    Formatters.number.renderString((f) =>
                      f.formatNumber(Math.round(item.quantity * 10) / 10, 1, 6, true)
                    )
                  }}
                </span>
              </span>
            </li>
          </ul>
        </div>
        <HalloweenGenericButton :disabled="!isCanConfirm" @click="handleConfirm">{{
          $t('halloweenButtonNext')
        }}</HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { CHARACTER_STAT, CHARACTER_STATUS, CharacterCard } from '~/entities/halloween';
import type { ParsedCharacterData } from '~/types/halloween/expedition';
import { HalloweenItem, type ItemWithQuantity } from '~/types/halloween/items';
import { Formatters } from '~/utils/formatters/formatters';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';

const isOpen = defineModel<boolean>({ required: true });

const halloween = useHalloweenStore();
const { availableCharacters: stateCharacters } = storeToRefs(halloween);

const emit = defineEmits<{
  confirm: [ParsedCharacterData[]];
  back: [];
}>();

const availableCharacters = computed(() =>
  stateCharacters.value.map((character) => ({
    name: `halloweenCharacter${character.characterType}`,
    type: character.characterType,
    initialData: character,
    cost: [
      halloweenItemRegistry.withQuantity(HalloweenItem.GoldOre, character.goldOrePrice),
      halloweenItemRegistry.withQuantity(HalloweenItem.Pearl, character.pearlPrice)
    ],
    status: character.available ? CHARACTER_STATUS.AVAILABLE : CHARACTER_STATUS.HIRED,
    stats: {
      [CHARACTER_STAT.HEALTH]: character.health,
      [CHARACTER_STAT.DEFENCE]: character.defense,
      [CHARACTER_STAT.ATTACK]: character.attack
    }
  }))
);

const totalPrice = computed(() => {
  const resultingArray: ItemWithQuantity[] = [
    {
      ...halloweenItemRegistry.get(HalloweenItem.GoldOre),
      quantity: 0
    },
    {
      ...halloweenItemRegistry.get(HalloweenItem.Pearl),
      quantity: 0
    }
  ];

  halloween.$state.runtimeSelectedCharacters.forEach((character) => {
    const tokens = [
      halloweenItemRegistry.withQuantity(HalloweenItem.GoldOre, character.goldOrePrice),
      halloweenItemRegistry.withQuantity(HalloweenItem.Pearl, character.pearlPrice)
    ];

    tokens.forEach((value) => {
      const currentIndex = resultingArray.findIndex((i) => i.id === value.id);
      if (currentIndex === -1) resultingArray.push({ ...value });
      else {
        resultingArray[currentIndex].quantity += value.quantity;
      }
    });
  });

  return resultingArray;
});

const isCanConfirm = computed(() => halloween.$state.runtimeSelectedCharacters.length > 0);

function handleConfirm() {
  emit('confirm', halloween.$state.runtimeSelectedCharacters);
}
</script>
